/**
 * NodeView Component - React Flow canvas for beat nodes
 *
 * Selection Strategy:
 * - selectedNodeIds accessed in flowNodes memo but NOT in dependencies
 * - This prevents infinite loops from selectedNodeIds changes → memo recompute → new objects → StoreUpdater → loop
 * - Selection changes handled via onSelectionChange callback
 * - 'select' type changes filtered out in onNodesChange to prevent circular updates
 */

import { useCallback, useMemo, useRef, useEffect, useState, CSSProperties } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  ControlButton,
  Node,
  NodeChange,
  BackgroundVariant,
  useReactFlow,
  ReactFlowProvider,
  NodeTypes,
  OnSelectionChangeParams,
  SelectionMode,
  type Viewport,
} from '@xyflow/react';

import '@xyflow/react/dist/style.css';

import BeatNode from './BeatNode';
import CommentBoxNode from './CommentBoxNode';
import { useProjectStore } from '@/stores/projectStore';
import { useFocusedNodeId } from '@/stores/playbackStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { useViewStore } from '@/stores/viewStore';
import { BeatNodeData, CommentBoxData } from '@/types';
import { resizeImageForImport } from '@/utils/imageProcessing';
import { NODE_WARNING_COUNT, NODE_MAX_COUNT } from '@/constants/app';
import { blobManager } from '@/utils/blobManager';

/**
 * Type alias for our nodes in React Flow format
 * Supports both beat nodes and comment box nodes
 */
type FlowNodeData = BeatNodeData | CommentBoxData;
type FlowNode = Node<FlowNodeData>;

function NodeViewInner() {
  const nodes = useProjectStore((state) => state.nodes);
  const commentBoxes = useProjectStore((state) => state.commentBoxes);
  const addNode = useProjectStore((state) => state.addNode);
  const updateNode = useProjectStore((state) => state.updateNode);
  const updateNodePosition = useProjectStore((state) => state.updateNodePosition);
  const deleteNode = useProjectStore((state) => state.deleteNode);
  const duplicateNode = useProjectStore((state) => state.duplicateNode);
  const duplicateSelectedNodesInPlace = useProjectStore((state) => state.duplicateSelectedNodesInPlace);
  const selectNode = useProjectStore((state) => state.selectNode);
  const selectNodes = useProjectStore((state) => state.selectNodes);
  const selectedNodeIds = useProjectStore((state) => state.selectedNodeIds);
  const clearTitleSelection = useProjectStore((state) => state.clearTitleSelection);
  const copySelectedNodes = useProjectStore((state) => state.copySelectedNodes);
  const pasteNodes = useProjectStore((state) => state.pasteNodes);
  const setLastClickedPosition = useProjectStore((state) => state.setLastClickedPosition);
  const lastClickedPosition = useProjectStore((state) => state.lastClickedPosition);
  const clipboard = useProjectStore((state) => state.clipboard);
  const saveHistory = useProjectStore((state) => state.saveHistory);
  const undo = useProjectStore((state) => state.undo);
  const redo = useProjectStore((state) => state.redo);
  const createCommentBox = useProjectStore((state) => state.createCommentBox);
  const updateCommentBox = useProjectStore((state) => state.updateCommentBox);
  const deleteCommentBox = useProjectStore((state) => state.deleteCommentBox);
  const projectPath = useProjectStore((state) => state.projectPath);
  const focusedNodeId = useFocusedNodeId();
  const showNotification = useNotificationStore((state) => state.showNotification);

  const beatsViewport = useViewStore((state) => state.beatsViewport);
  const setBeatsViewport = useViewStore((state) => state.setBeatsViewport);

  const { screenToFlowPosition, fitView } = useReactFlow();

  // Track if we're currently duplicating a node
  const isDuplicatingRef = useRef<string | null>(null);
  const dragStartPosRef = useRef<{ x: number; y: number } | null>(null);

  // Track multi-node drag
  const multiDragStartPositions = useRef<Map<string, { x: number; y: number }>>(new Map());

  // Register our custom node types
  const nodeTypes: NodeTypes = useMemo(() => ({ 
    beat: BeatNode,
    commentBox: CommentBoxNode,
  }), []);

  // Map our stored nodes to React Flow format with selected state
  // Filter out nodes that are on the shelf
  // NOTE: selectedNodeIds IS included to sync external selection changes (timeline, etc)
  // Infinite loop prevented by: onNodesChange filters 'select' AND onSelectionChange uses ref check
  const flowNodes = useMemo(
    () => {
      const beatNodes = nodes
        .filter((node) => !node.data.onShelf)
        .map((node) => ({
          ...node,
          selected: selectedNodeIds.includes(node.id),
        }));

      // Add comment boxes with callbacks - ensure same structure as beat nodes
      const commentNodes = commentBoxes.map((box) => {
        const node: any = {
          id: box.id,
          type: 'commentBox',
          position: box.position,
          data: {
            ...box.data,
            onUpdate: updateCommentBox,
            onDelete: deleteCommentBox,
          },
          selected: selectedNodeIds.includes(box.id),
          draggable: true, // Must be true for dragHandle to work
          dragHandle: '.comment-box-drag-handle', // Only draggable from this element
        };

        // Set dimensions using measured property (React Flow v12)
        node.measured = {
          width: box.width || 400,
          height: box.height || 300,
        };

        // Also set style for initial render - always behind nodes
        node.style = {
          width: box.width || 400,
          height: box.height || 300,
          zIndex: -1, // Always render behind, even when selected
          pointerEvents: 'none', // Make the wrapper transparent to mouse events
        };

        // Set z-index separately (for non-selected state)
        node.zIndex = -1;

        return node;
      });

      // Combine both types - comment boxes first (render behind beats)
      return [...commentNodes, ...beatNodes];
    },
    [nodes, commentBoxes, selectedNodeIds, updateCommentBox, deleteCommentBox]
  );

  // Track previous selection to prevent infinite loops
  const prevSelectionRef = useRef<string[]>([]);

  // Update ref when selectedNodeIds changes (but after onSelectionChange check)
  useEffect(() => {
    prevSelectionRef.current = selectedNodeIds;
  }, [selectedNodeIds]);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      // Filter out 'select' changes - they're handled by onSelectionChange
      // This prevents circular updates when selectedNodeIds changes
      const filteredChanges = changes.filter(change => change.type !== 'select');

      if (filteredChanges.length === 0) return;

      // Check if there are any remove changes - save history before deletion
      const hasRemove = filteredChanges.some((change) => change.type === 'remove');
      if (hasRemove) {
        saveHistory();
      }

      // Process position, dimensions, and remove changes
      // Selection changes are now handled by onSelectionChange
      filteredChanges.forEach((change) => {
        if (change.type === 'position' && change.position && change.id) {
          // Check if this change is for a comment box
          const isCommentBox = commentBoxes.some(box => box.id === change.id);
          
          if (isCommentBox) {
            // Comment box position change
            updateCommentBox(change.id, { position: change.position });
          } else {
            // Beat node position change
            // If we're duplicating, update the duplicated node instead
            if (isDuplicatingRef.current) {
              updateNodePosition(isDuplicatingRef.current, change.position);
            } else {
              // Check if this is a multi-node drag
              const draggedNodeStartPos = multiDragStartPositions.current.get(change.id);
              if (draggedNodeStartPos && multiDragStartPositions.current.size > 1) {
                // Calculate the delta from the dragged node's starting position
                const dx = change.position.x - draggedNodeStartPos.x;
                const dy = change.position.y - draggedNodeStartPos.y;

                // Apply the delta to all selected nodes
                multiDragStartPositions.current.forEach((startPos, nodeId) => {
                  updateNodePosition(nodeId, {
                    x: startPos.x + dx,
                    y: startPos.y + dy,
                  });
                });
              } else {
                // Single node drag
                updateNodePosition(change.id, change.position);
              }
            }
          }
        } else if (change.type === 'dimensions' && change.id) {
          // Handle comment box resizing
          const isCommentBox = commentBoxes.some(box => box.id === change.id);
          if (isCommentBox && change.dimensions) {
            updateCommentBox(change.id, {
              width: change.dimensions.width,
              height: change.dimensions.height,
            });
          }
        } else if (change.type === 'remove' && change.id) {
          const isCommentBox = commentBoxes.some(box => box.id === change.id);
          if (isCommentBox) {
            deleteCommentBox(change.id);
          } else {
            deleteNode(change.id);
          }
        }
      });
    },
    [updateNodePosition, deleteNode, saveHistory, commentBoxes, updateCommentBox, deleteCommentBox]
  );

  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: FlowNode) => {
      // Track the click position for paste operations
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      setLastClickedPosition(position);

      // Clear title selection when selecting a beat node
      clearTitleSelection();

      // Handle shift+click for multi-selection toggle
      if (event.shiftKey) {
        event.stopPropagation();
        if (selectedNodeIds.includes(node.id)) {
          // Deselect this node
          selectNodes(selectedNodeIds.filter(id => id !== node.id));
        } else {
          // Add this node to selection
          selectNodes([...selectedNodeIds, node.id]);
        }
      } else {
        // Normal click - select only this node
        selectNode(node.id);
      }
    },
    [selectedNodeIds, selectNode, selectNodes, screenToFlowPosition, setLastClickedPosition, clearTitleSelection]
  );

  const onNodeDragStart = useCallback(
    (event: React.MouseEvent, node: FlowNode) => {
      // Save history before drag starts
      saveHistory();

      // If the dragged node is not in the current selection, select only it
      // This allows dragging a single node even when multiple are selected
      if (!selectedNodeIds.includes(node.id)) {
        selectNode(node.id);
      }
      // If it's already selected, keep the multi-selection (allows dragging multiple nodes)

      // If Alt key is pressed, prepare to duplicate
      if (event.altKey) {
        if (selectedNodeIds.length === 1) {
          // Single node duplication (existing behavior)
          dragStartPosRef.current = node.position;
          isDuplicatingRef.current = node.id;
        } else {
          // Multi-node duplication - duplicate in place, then track for dragging
          const newNodeIds = duplicateSelectedNodesInPlace();
          // Set up multi-drag for the duplicated nodes
          multiDragStartPositions.current.clear();
          nodes.forEach((n) => {
            if (newNodeIds.includes(n.id)) {
              multiDragStartPositions.current.set(n.id, { ...n.position });
            }
          });
        }
      } else {
        // Track starting positions of all selected nodes for multi-drag
        multiDragStartPositions.current.clear();
        nodes.forEach((n) => {
          if (selectedNodeIds.includes(n.id)) {
            multiDragStartPositions.current.set(n.id, { ...n.position });
          }
        });
      }
    },
    [saveHistory, selectedNodeIds, selectNode, nodes, duplicateSelectedNodesInPlace]
  );

  const onNodeDrag = useCallback(
    (event: React.MouseEvent, node: FlowNode) => {
      // If we're set to duplicate but haven't created the duplicate yet
      if (isDuplicatingRef.current === node.id && dragStartPosRef.current) {
        // Check if we've moved enough to trigger duplication (at least 5 pixels)
        const dx = Math.abs(node.position.x - dragStartPosRef.current.x);
        const dy = Math.abs(node.position.y - dragStartPosRef.current.y);

        if (dx > 5 || dy > 5) {
          // Single node duplication
          const duplicated = duplicateNode(node.id, node.position);
          if (duplicated) {
            // Now track the duplicated node for position updates
            isDuplicatingRef.current = duplicated.id;
            selectNode(duplicated.id);
          }
        }
      }
    },
    [duplicateNode, selectNode]
  );

  const onNodeDragStop = useCallback(() => {
    // Clear duplication state
    isDuplicatingRef.current = null;
    dragStartPosRef.current = null;
    // Clear multi-drag tracking
    multiDragStartPositions.current.clear();
  }, []);

  // Helper to trigger script input focus for a newly created node
  const focusScriptInputForNode = useCallback((nodeId: string) => {
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('focusScriptInput', { detail: { nodeId } }));
    }, 50);
  }, []);

  const onDoubleClickHandler = useCallback(
    (event: React.MouseEvent) => {
      const target = event.target as HTMLElement;
      // Don't create nodes when clicking on controls, minimap, or existing nodes
      if (target.closest('.react-flow__controls') ||
          target.closest('.react-flow__minimap') ||
          target.closest('.react-flow__node')) {
        return;
      }

      // Check node count limits
      const currentNodeCount = nodes.length;
      
      if (currentNodeCount >= NODE_MAX_COUNT) {
        alert(`Maximum ${NODE_MAX_COUNT} beats reached. Consider splitting your project into multiple files for better performance.`);
        return;
      }
      
      if (currentNodeCount >= NODE_WARNING_COUNT && currentNodeCount < NODE_MAX_COUNT) {
        showNotification(`⚠️ Large project (${currentNodeCount} beats). May affect performance.`, 'warning', 5000);
      }

      // Save history before creating new node
      saveHistory();

      // Node dimensions (from BeatNode.tsx)
      const NODE_WIDTH = 180;
      const NODE_HEIGHT = 200;

      // Get click position
      const clickPosition = screenToFlowPosition({ x: event.clientX, y: event.clientY });

      // Offset to center the node on the click position
      const centeredPosition = {
        x: clickPosition.x - NODE_WIDTH / 2,
        y: clickPosition.y - NODE_HEIGHT / 2,
      };

      const newNode = addNode(centeredPosition);
      selectNode(newNode.id);

      // Auto-focus script input for immediate typing
      focusScriptInputForNode(newNode.id);
    },
    [addNode, selectNode, screenToFlowPosition, saveHistory, nodes.length, focusScriptInputForNode]
  );

  const onPaneClick = useCallback((event: React.MouseEvent) => {
    // Track the click position for paste operations
    const position = screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });
    setLastClickedPosition(position);

    // Clear selection when clicking on the pane
    selectNodes([]);
    clearTitleSelection();
  }, [selectNodes, screenToFlowPosition, setLastClickedPosition, clearTitleSelection]);

  const onSelectionChange = useCallback(
    (params: OnSelectionChangeParams) => {
      // Sync React Flow's selection with our Zustand store
      const newSelection = params.nodes.map((node) => node.id).sort();
      const prevSelection = [...prevSelectionRef.current].sort();

      // Only update if selection actually changed (prevent infinite loop)
      if (JSON.stringify(newSelection) !== JSON.stringify(prevSelection)) {
        prevSelectionRef.current = params.nodes.map((node) => node.id);
        selectNodes(params.nodes.map((node) => node.id));
      }
    },
    [selectNodes]
  );

  // Save viewport on changes (zoom, pan)
  const onViewportChange = useCallback(
    (viewport: Viewport) => {
      setBeatsViewport(viewport);
    },
    [setBeatsViewport]
  );

  // Handle bulk image import via drag and drop
  const handlePaneDrop = useCallback(
    async (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      // Get all dropped files
      const files = Array.from(event.dataTransfer.files);
      
      // Filter for image files only
      const imageFiles = files.filter(f =>
        f.type.startsWith('image/') ||
        /\.(png|jpe?g|gif|webp|bmp)$/i.test(f.name)
      );

      if (imageFiles.length === 0) return;

      // Check node count limits
      const currentNodeCount = nodes.length;
      const newNodeCount = currentNodeCount + imageFiles.length;
      
      if (newNodeCount > NODE_MAX_COUNT) {
        const allowedCount = NODE_MAX_COUNT - currentNodeCount;
        if (allowedCount <= 0) {
          alert(`Maximum ${NODE_MAX_COUNT} beats reached. Cannot import more images.`);
          return;
        }
        alert(`You can only import ${allowedCount} more images (current: ${currentNodeCount}, max: ${NODE_MAX_COUNT}).`);
        return;
      }
      
      if (newNodeCount >= NODE_WARNING_COUNT && currentNodeCount < NODE_WARNING_COUNT) {
        showNotification(`⚠️ Importing ${imageFiles.length} images will create ${newNodeCount} beats. May affect performance.`, 'warning', 5000);
      }

      // Save history before bulk operation
      saveHistory();

      // Get drop position
      const dropPosition = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      // Layout configuration
      const NODE_WIDTH = 180;
      const NODE_HEIGHT = 200;
      const COLUMNS = 3;
      const SPACING_X = 220; // NODE_WIDTH (180) + 40px gap
      const SPACING_Y = 240; // NODE_HEIGHT (200) + 40px gap

      // Create beats in grid layout
      const newNodeIds: string[] = [];
      for (let i = 0; i < imageFiles.length; i++) {
        const row = Math.floor(i / COLUMNS);
        const col = i % COLUMNS;
        
        // Calculate grid position
        const gridX = dropPosition.x + (col * SPACING_X);
        const gridY = dropPosition.y + (row * SPACING_Y);
        
        // Center the node on the grid position
        const position = {
          x: gridX - NODE_WIDTH / 2,
          y: gridY - NODE_HEIGHT / 2,
        };

        // Create beat
        const newNode = addNode(position);
        newNodeIds.push(newNode.id);

        // Process image - resize first for performance
        const imageFile = imageFiles[i];
        
        try {
          // Resize image to optimize storage and performance
          const resizedBlob = await resizeImageForImport(imageFile);
          
          // Check if we have Electron API with file paths
          const filePath = (imageFile as File & { path?: string }).path;
          
          if (filePath && window.electronAPI && projectPath) {
            // Electron mode: Use resized blob as URL for copyImage
            const resizedUrl = URL.createObjectURL(resizedBlob);
            const projectDir = projectPath.replace(/[/\\][^/\\]+$/, '');
            const result = await window.electronAPI.copyImage(resizedUrl, projectDir);
            URL.revokeObjectURL(resizedUrl);

            if (result.success && result.relativePath) {
              updateNode(newNode.id, { imagePath: result.relativePath });
            }
          } else {
            // Browser mode: create blob URL from resized image
            const blobUrl = blobManager.create(resizedBlob, `node-image:${newNode.id}`);
            const result = await window.electronAPI?.copyImage(blobUrl, '');

            if (result?.success && result.relativePath) {
              updateNode(newNode.id, { imagePath: result.relativePath });
            }
          }
        } catch (error) {
          console.error('Failed to process image:', error);
          // Skip this image and continue with others
        }
      }

      // Select all newly created beats for easy repositioning
      selectNodes(newNodeIds);

      // Auto-focus script input on the last created node
      if (newNodeIds.length > 0) {
        const lastNodeId = newNodeIds[newNodeIds.length - 1];
        focusScriptInputForNode(lastNodeId);
      }
    },
    [addNode, updateNode, saveHistory, screenToFlowPosition, selectNodes, projectPath, focusScriptInputForNode]
  );

  const handlePaneDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
  }, []);

  // Frame selected node - zoom and center on the selected node
  const frameSelectedNode = useCallback(() => {
    if (selectedNodeIds.length > 0) {
      // Frame all selected nodes if multiple are selected
      // Use same zoom level as focus on playing beat (padding: 2.5)
      fitView({
        nodes: selectedNodeIds.map(id => ({ id })),
        duration: 300,
        padding: 2.5,
      });
    }
  }, [selectedNodeIds, fitView]);

  // Rapid beat creation - B key creates beat after focused node
  const handleRapidBeatCreation = useCallback(() => {
    // Check node count limit
    if (nodes.length >= NODE_MAX_COUNT) {
      showNotification(`Maximum ${NODE_MAX_COUNT} nodes reached`, 'error', 3000);
      return;
    }

    saveHistory();

    const SPACING = 200;
    const activeNodes = nodes.filter((n) => !n.data.onShelf);
    const sortedNodes = [...activeNodes].sort((a, b) => a.position.x - b.position.x);

    let targetX = 0;
    let targetY = 0;

    // If there's a focused node, insert after it
    if (focusedNodeId) {
      const focusedIndex = sortedNodes.findIndex((n) => n.id === focusedNodeId);

      if (focusedIndex !== -1) {
        const focusedNode = sortedNodes[focusedIndex];
        targetX = focusedNode.position.x + SPACING;
        targetY = focusedNode.position.y;

        // Shift all nodes after the focused node to make room
        for (let i = focusedIndex + 1; i < sortedNodes.length; i++) {
          const currentNode = sortedNodes[i];
          const expectedMinX = targetX + (i - focusedIndex) * SPACING;

          if (currentNode.position.x < expectedMinX) {
            updateNodePosition(currentNode.id, {
              x: expectedMinX,
              y: currentNode.position.y,
            });
          }
        }
      }
    } else if (sortedNodes.length > 0) {
      // No focused node, place at the end
      const lastNode = sortedNodes[sortedNodes.length - 1];
      targetX = lastNode.position.x + SPACING;
      targetY = lastNode.position.y;
    }

    // Create the new beat
    const newNode = addNode({ x: targetX, y: targetY });

    // Select the new node
    selectNode(newNode.id);

    // Auto-focus script input for immediate typing
    focusScriptInputForNode(newNode.id);

    showNotification('New beat created', 'info', 1500);
  }, [nodes, focusedNodeId, addNode, selectNode, updateNodePosition, saveHistory, showNotification, focusScriptInputForNode]);

  // Keyboard shortcuts handler
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger when typing in inputs
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      // F key = focus on current beat node (playing)
      // Shift+F = frame selected node
      if (event.key === 'f' || event.key === 'F') {
        if (event.shiftKey) {
          // Shift+F = frame selected node
          if (selectedNodeIds.length > 0) {
            event.preventDefault();
            frameSelectedNode();
          }
        } else if (focusedNodeId) {
          // F (no shift) = focus on current beat node (during playback)
          event.preventDefault();
          // Fit view to the focused node with more padding (more zoomed out)
          fitView({
            nodes: [{ id: focusedNodeId }],
            duration: 300,
            padding: 2.5,
          });
        }
      }

      // Ctrl+Z / Cmd+Z = Undo
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z' && !event.shiftKey) {
        event.preventDefault();
        undo();
      }

      // Ctrl+Shift+Z / Cmd+Shift+Z OR Ctrl+Y / Cmd+Y = Redo
      if (
        ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z' && event.shiftKey) ||
        ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'y')
      ) {
        event.preventDefault();
        redo();
      }

      // Ctrl+C / Cmd+C = Copy selected nodes
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'c') {
        if (selectedNodeIds.length > 0) {
          event.preventDefault();
          copySelectedNodes();
        }
      }

      // C key (without modifiers) = Create comment box around selected nodes
      if (event.key.toLowerCase() === 'c' && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
        if (selectedNodeIds.length > 0) {
          event.preventDefault();
          const newBox = createCommentBox();
          if (newBox) {
            showNotification('Comment box created', 'info', 2000);
          }
        }
      }

      // B key = Create new beat after current beat and focus script input
      if (event.key.toLowerCase() === 'b' && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        handleRapidBeatCreation();
      }

      // Ctrl+V / Cmd+V = Handled by paste event handler below
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [focusedNodeId, fitView, undo, redo, selectedNodeIds, copySelectedNodes, pasteNodes, createCommentBox, showNotification, frameSelectedNode, handleRapidBeatCreation]);

  // Paste handler for clipboard images
  useEffect(() => {
    console.log('[Clipboard] Setting up paste event listener...');
    
    // Test handler to see if ANY keydown works
    const testKeyHandler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'v') {
        console.log('[Clipboard] Ctrl+V detected via keydown!');
      }
    };
    
    const handlePaste = async (event: ClipboardEvent) => {
      console.log('[Clipboard] Paste event fired!', event);

      // Don't trigger when pasting in inputs
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        console.log('[Clipboard] Ignoring paste in input field');
        return;
      }

      const items = event.clipboardData?.items;
      if (!items) {
        console.log('[Clipboard] No clipboard items found');
        return;
      }
      
      console.log('[Clipboard] Clipboard items:', items.length);
      for (let i = 0; i < items.length; i++) {
        console.log(`[Clipboard] Item ${i}: type="${items[i].type}", kind="${items[i].kind}"`);
      }

      // PRIORITY 1: Check internal clipboard first (copied nodes via Ctrl+C)
      const hasInternalNodes = clipboard.length > 0;
      console.log('[Clipboard] Internal clipboard has nodes:', hasInternalNodes, clipboard.length);

      // Check if there's an image in the system clipboard
      let hasImage = false;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          hasImage = true;
          break;
        }
      }
      console.log('[Clipboard] System clipboard has image:', hasImage);

      // If we have copied nodes, paste them (this is most recent Ctrl+C action)
      if (hasInternalNodes) {
        console.log('[Clipboard] Pasting nodes from internal clipboard (takes priority)...');
        event.preventDefault();
        pasteNodes();
        return;
      }

      // PRIORITY 2: No copied nodes - check for images
      if (!hasImage) {
        console.log('[Clipboard] Nothing to paste (no nodes or images in clipboard)');
        return;
      }
      
      console.log('[Clipboard] No copied nodes, will paste image from system clipboard');

      // Now handle image pasting (we know hasImage is true here)
      const firstSelectedId = selectedNodeIds[0];
      if (firstSelectedId) {
        // Image + node selected: Paste to selected node
        console.log('[Clipboard] Found image and selected node, pasting image...');
        event.preventDefault();
        
        for (let i = 0; i < items.length; i++) {
          if (items[i].type.startsWith('image/')) {
          event.preventDefault();
          const blob = items[i].getAsFile();
          if (!blob) continue;

          try {
            console.log('[Clipboard] Processing image paste...');
            
            // Convert blob to File for resizing (resizeImageForImport expects File)
            const file = new File([blob], `clipboard-${Date.now()}.png`, { type: blob.type });
            console.log('[Clipboard] File created:', file.name, file.size, 'bytes');
            
            // Resize image to optimize storage and performance
            const resizedBlob = await resizeImageForImport(file);
            console.log('[Clipboard] Image resized:', resizedBlob.size, 'bytes');

            // Create blob URL from resized image
            const blobUrl = blobManager.create(resizedBlob, `clipboard-image:${firstSelectedId}`);
            console.log('[Clipboard] Blob URL created:', blobUrl);

            // Use the browser API to handle the image (works for both browser and Electron)
            if (!window.electronAPI) {
              console.error('[Clipboard] window.electronAPI is not available!');
              break;
            }
            
            const result = await window.electronAPI.copyImage(blobUrl, '');
            console.log('[Clipboard] copyImage result:', result);
            
            if (result?.success && result.relativePath) {
              console.log('[Clipboard] Updating node with imagePath:', result.relativePath);
              updateNode(firstSelectedId, { imagePath: result.relativePath });
              console.log('[Clipboard] Image successfully pasted!');
            } else {
              console.error('[Clipboard] copyImage failed:', result);
            }
            } catch (error) {
              console.error('[Clipboard] Failed to process clipboard image:', error);
            }
            break;
          }
        }
      } else {
        // Image + no node selected: Create new node and paste image
        console.log('[Clipboard] Found image but no node selected, creating new node...');
        event.preventDefault();
        
        // Save history before creating node
        saveHistory();
        
        // Get position from last clicked location, or center of viewport
        const clickPosition = lastClickedPosition || { x: 0, y: 0 };
        
        // Node dimensions (from BeatNode.tsx)
        const NODE_WIDTH = 180;
        const NODE_HEIGHT = 200;
        
        // Center the node on the click position
        const centeredPosition = {
          x: clickPosition.x - NODE_WIDTH / 2,
          y: clickPosition.y - NODE_HEIGHT / 2,
        };
        
        // Create new node
        const newNode = addNode(centeredPosition);
        console.log('[Clipboard] New node created:', newNode.id);
        
        // Now paste the image to the new node
        for (let i = 0; i < items.length; i++) {
          if (items[i].type.startsWith('image/')) {
            const blob = items[i].getAsFile();
            if (!blob) continue;

            try {
              console.log('[Clipboard] Processing image paste to new node...');
              
              // Convert blob to File for resizing
              const file = new File([blob], `clipboard-${Date.now()}.png`, { type: blob.type });

              // Resize image to optimize storage and performance
              const resizedBlob = await resizeImageForImport(file);

              // Create blob URL from resized image
              const blobUrl = blobManager.create(resizedBlob, `clipboard-new-node:${newNode.id}`);

              // Use the browser API to handle the image
              if (!window.electronAPI) {
                console.error('[Clipboard] window.electronAPI is not available!');
                break;
              }
              
              const result = await window.electronAPI.copyImage(blobUrl, '');
              
              if (result?.success && result.relativePath) {
                console.log('[Clipboard] Updating new node with imagePath:', result.relativePath);
                updateNode(newNode.id, { imagePath: result.relativePath });
                selectNode(newNode.id); // Select the newly created node

                // Auto-focus script input for immediate typing
                focusScriptInputForNode(newNode.id);

                console.log('[Clipboard] Image successfully pasted to new node!');
              } else {
                console.error('[Clipboard] copyImage failed:', result);
              }
            } catch (error) {
              console.error('[Clipboard] Failed to process clipboard image:', error);
            }
            break;
          }
        }
      }
    };

    // Add keydown test and paste handler (only to window, not both!)
    window.addEventListener('keydown', testKeyHandler);
    window.addEventListener('paste', handlePaste);
    console.log('[Clipboard] Paste event listener attached to window');
    
    return () => {
      console.log('[Clipboard] Removing paste event listener');
      window.removeEventListener('keydown', testKeyHandler);
      window.removeEventListener('paste', handlePaste);
    };
  }, [selectedNodeIds, updateNode, pasteNodes]);

  // Track if user has dismissed the help tooltip
  const [showHelpTooltip, setShowHelpTooltip] = useState(() => {
    return typeof window !== 'undefined'
      ? localStorage.getItem('emberboard-hideHelpTooltip') !== 'true'
      : true;
  });

  // Track if we're hovering over empty space
  const [isHoveringEmpty, setIsHoveringEmpty] = useState(false);

  // Track when we should show the tooltip (after delay)
  const [showTooltipDelayed, setShowTooltipDelayed] = useState(false);

  // Hide tooltip after 8 nodes
  const shouldShowTooltip = showHelpTooltip && nodes.length < 8;

  // Delay showing tooltip
  useEffect(() => {
    if (shouldShowTooltip && isHoveringEmpty) {
      const timer = setTimeout(() => {
        setShowTooltipDelayed(true);
      }, 1000); // 1 second delay
      return () => clearTimeout(timer);
    } else {
      setShowTooltipDelayed(false);
    }
  }, [shouldShowTooltip, isHoveringEmpty]);

  const containerStyles: CSSProperties = {
    width: '100%',
    height: '100%',
    backgroundColor: 'var(--nb-bg-secondary)',
    position: 'relative',
  };

  const tooltipStyles: CSSProperties = {
    position: 'absolute',
    top: '20px',
    right: '20px',
    backgroundColor: 'var(--nb-bg-tertiary)',
    border: '1px solid var(--nb-border-primary)',
    borderRadius: 'var(--nb-radius-sm)',
    padding: '12px 16px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    maxWidth: '280px',
    fontSize: 'var(--nb-font-size-sm)',
    color: 'var(--nb-text-primary)',
    zIndex: 100,
    opacity: shouldShowTooltip && isHoveringEmpty && showTooltipDelayed ? 0.5 : 0,
    transition: 'opacity 0.5s ease-in-out',
    pointerEvents: 'none',
  };

  const tooltipCloseStyles: CSSProperties = {
    position: 'absolute',
    top: '8px',
    right: '8px',
    background: 'none',
    border: 'none',
    fontSize: '16px',
    cursor: 'pointer',
    color: 'var(--nb-text-secondary)',
    padding: '0 4px',
    pointerEvents: 'auto',
  };

  return (
    <div
      style={containerStyles}
      onMouseMove={(e) => {
        // Check if mouse is over empty space (not over any node)
        const target = e.target as HTMLElement;
        if (!target.closest('.react-flow__node') && !target.closest('.react-flow__edge')) {
          setIsHoveringEmpty(true);
        } else {
          setIsHoveringEmpty(false);
        }
      }}
      onMouseLeave={() => setIsHoveringEmpty(false)}
    >
      <ReactFlow
        nodes={flowNodes}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onNodeClick={onNodeClick}
        onNodeDragStart={onNodeDragStart}
        onNodeDrag={onNodeDrag}
        onNodeDragStop={onNodeDragStop}
        onPaneClick={onPaneClick}
        onDoubleClick={onDoubleClickHandler}
        onSelectionChange={onSelectionChange}
        defaultViewport={beatsViewport}
        onViewportChange={onViewportChange}
        onDrop={handlePaneDrop}
        onDragOver={handlePaneDragOver}
        selectionOnDrag
        panOnDrag
        noDragClassName="nodrag" // Elements with this class won't trigger node drag
        selectionKeyCode="Shift"
        multiSelectionKeyCode="Shift"
        selectionMode={SelectionMode.Partial}
        zoomOnScroll={true}
        zoomOnPinch={true}
        zoomOnDoubleClick={false}
        minZoom={0.05}
        maxZoom={4}
        style={{ background: 'var(--nb-bg-secondary)' }}
        deleteKeyCode="Delete"
        nodesDraggable={true}
        nodesConnectable={false}
        elementsSelectable={true}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="var(--nb-border-primary)" />
        <Controls
          showInteractive={false}
          orientation="horizontal"
          style={{
            backgroundColor: 'var(--nb-bg-tertiary)',
            border: '1px solid var(--nb-border-primary)',
            borderRadius: 'var(--nb-radius-sm)',
            left: '10px',
            bottom: '10px',
            transform: 'scale(0.75)',
            transformOrigin: 'bottom left',
          }}
        >
          <ControlButton
            onClick={frameSelectedNode}
            disabled={selectedNodeIds.length === 0}
            title="Frame selected node (Shift+F)"
            aria-label="Frame selected node"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </ControlButton>
        </Controls>
      </ReactFlow>
      {nodes.length === 0 && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: 'var(--nb-text-muted)',
          textAlign: 'center',
          pointerEvents: 'none'
        }}>
          <p>Double-click to create a beat node</p>
          <p style={{ fontSize: 'var(--nb-font-size-xs)', marginTop: 'var(--nb-space-xs)' }}>
            Press <kbd style={{
              backgroundColor: 'var(--nb-bg-tertiary)',
              padding: '2px 6px',
              borderRadius: 'var(--nb-radius-xs)',
              border: '1px solid var(--nb-border-primary)',
              fontFamily: 'monospace'
            }}>B</kbd> to create a beat after the current one
          </p>
          <p style={{ fontSize: 'var(--nb-font-size-xs)', marginTop: 'var(--nb-space-xs)' }}>
            Drag and drop images to create beats automatically
          </p>
        </div>
      )}

      {/* Help tooltip for when nodes exist but user hovers over empty space */}
      {showHelpTooltip && (
        <div style={tooltipStyles}>
          <button
            style={tooltipCloseStyles}
            onClick={() => {
              localStorage.setItem('emberboard-hideHelpTooltip', 'true');
              setShowHelpTooltip(false);
            }}
            title="Dismiss help tooltip"
          >
            ×
          </button>
          <p style={{ margin: 0, fontWeight: 600, marginBottom: '8px' }}>Quick Tips:</p>
          <ul style={{ margin: 0, paddingLeft: '16px', fontSize: 'var(--nb-font-size-xs)' }}>
            <li style={{ marginBottom: '6px' }}>Double-click to add a new beat</li>
            <li style={{ marginBottom: '6px' }}>
              Press <kbd style={{
                backgroundColor: 'var(--nb-bg-primary)',
                padding: '1px 4px',
                borderRadius: 'var(--nb-radius-xs)',
                border: '1px solid var(--nb-border-primary)',
                fontFamily: 'monospace',
                fontSize: 'var(--nb-font-size-xs)'
              }}>B</kbd> to create after current beat
            </li>
            <li style={{ marginBottom: '6px' }}>Drag in image to create beat</li>
            <li style={{ marginBottom: '6px' }}>Paste image from clipboard</li>
            <li>Hold <kbd style={{
              backgroundColor: 'var(--nb-bg-primary)',
              padding: '1px 4px',
              borderRadius: 'var(--nb-radius-xs)',
              border: '1px solid var(--nb-border-primary)',
              fontFamily: 'monospace',
              fontSize: 'var(--nb-font-size-xs)'
            }}>Alt</kbd> + drag to duplicate</li>
          </ul>
        </div>
      )}
    </div>
  );
}

function NodeView() {
  return (
    <ReactFlowProvider>
      <NodeViewInner />
    </ReactFlowProvider>
  );
}

export default NodeView;
